import type { CreateOrderFormValues } from '@eliadmin/core/src/pages/cart/page';

import { RadioGroup } from '@eliadmin/core/src/components/ui/radio-group';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { isNullish } from '@eliadmin/core/src/utils/misc-utils';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useCartActions } from '@eliadmin/core/src/hooks/cart/use-cart-actions';
import { useCartShipmentDestination } from '@eliadmin/core/src/hooks/cart/use-cart-shipment-destination';
import { useCartPaymentsState } from '@eliadmin/core/src/hooks/cart/use-cart-payments-state';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useIsMutating, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { CartStep } from '@eliadmin/core/src/hooks/cart/use-cart-stepper';
import { CartPayments, CartShipments } from '../detail';
import { CartSection } from '../cart-section';
import { CartNavigationButton } from '../continue-button';
import { SummaryCardItem } from './summary-card-item';
import { CartSummaryCard } from '../cart-summary-card';
import { Heading } from '../../ui/heading';
import { Field } from '@eliadmin/core/src/components/form/field';

export function CartOrderShipment() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);

  const { data: cart } = useCart();

  const form = useFormContext<CreateOrderFormValues>();

  const { countryId } = useCartShipmentDestination();
  const region = form.watch('region');

  const { setPaymentTypeMutation, setShipmentMutation } = useCartActions();
  const paymentState = useCartPaymentsState();
  const queryClient = useQueryClient();
  const pendingShipments = useIsMutating({ mutationKey: [CacheKey.SHIPMENTS] });
  const pendingPayments = useIsMutating({ mutationKey: [CacheKey.CART_PAYMENTS] });
  const isUpdating = pendingShipments > 0 || pendingPayments > 0;
  const isCartUpdating = () =>
    queryClient.isMutating({ mutationKey: [CacheKey.SHIPMENTS] }) > 0 ||
    queryClient.isMutating({ mutationKey: [CacheKey.CART_PAYMENTS] }) > 0;
  const canContinue = () =>
    cart.shipment?.id != null &&
    !isCartUpdating() &&
    paymentState.isPaymentReady(cart.paymentType?.id);

  const { data: countries } = useSuspenseQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const handlePaymentChange = (value: string) => {
    if (isCartUpdating() || !paymentState.isReady) return;
    if (isNullish(countryId)) {
      // eslint-disable-next-line no-console
      console.error('Country is not selected');
      return;
    }

    setPaymentTypeMutation.mutate({ locale, paymentId: value });
  };

  const handleShipmentChange = (value: string) => {
    if (isCartUpdating()) return;
    if (isNullish(countryId)) {
      // eslint-disable-next-line no-console
      console.error('Country is not selected');
      return;
    }

    setShipmentMutation.mutate({ countryId, regionId: region, locale, shipmentId: value });
  };

  return (
    <CartSection title={t('text571')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 md:flex-row md:justify-between',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-3 sm:gap-4 md:max-w-[807px] lg:gap-6">
          <RadioGroup
            name="shipments"
            disabled={isUpdating}
            value={cart.shipment?.id == null ? '' : String(cart.shipment.id)}
            onValueChange={handleShipmentChange}
            className="flex w-full flex-col items-start justify-start gap-4 rounded-[4px] bg-[#F9FAFB] p-3 sm:gap-5 sm:p-4 lg:gap-6  lg:p-6"
          >
            <div className="flex w-full flex-col items-start justify-start gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <Heading title={t('text125')} tag="h4" />
              <Field
                control={form.control}
                name={form.watch('different_delivery_address') ? 'country2.id' : 'country.id'}
                type="select"
                label={t('text602')}
                placeholder={t('text602')}
                options={countries?.data?.map(c => ({ value: String(c.id), label: c.name })) ?? []}
                className={{
                  wrapper: cn(
                    'flex w-full flex-col gap-2 sm:max-w-[300px]',
                    'ea-cart__shipment-country-form-item',
                  ),
                }}
              />
            </div>
            <CartShipments />
          </RadioGroup>
          <RadioGroup
            name="payments"
            disabled={isUpdating || !paymentState.isReady}
            value={cart.paymentType?.id == null ? '' : String(cart.paymentType.id)}
            onValueChange={handlePaymentChange}
            className="flex w-full flex-col items-start justify-start gap-4 rounded-[4px] bg-[#F9FAFB] p-3 sm:gap-5 sm:p-4 lg:gap-6 lg:p-6"
          >
            <Heading title={t('text1068')} tag="h4" />
            <CartPayments />
          </RadioGroup>
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-6 rounded-[4px] border border-border bg-transparent p-3 sm:p-4 md:max-w-[569px] lg:p-6">
          <Heading title={t('text2138')} tag="h4" />
          {cart.items?.map(item => (
            <SummaryCardItem key={item.id} item={item} />
          ))}
          <Separator />
          <CartSummaryCard className={cn('border-none bg-transparent p-0 sm:p-0 md:p-0 lg:p-0')} />
        </div>
      </div>
      <div
        className={cn(
          'custom-container',
          'flex flex-col-reverse items-center justify-end gap-3 sm:flex-row sm:justify-end sm:pr-0',
        )}
      >
        <CartNavigationButton
          step={CartStep.DETAIL}
          title={t('text1026')}
          variant={'secondary'}
          className="w-full whitespace-nowrap sm:w-fit"
          beforeIcon
        />
        <CartNavigationButton
          step={CartStep.ORDER}
          disabled={isUpdating || !canContinue()}
          onBeforeContinue={canContinue}
          title={t('text526')}
          className="w-full whitespace-nowrap sm:w-fit"
        />
      </div>
    </CartSection>
  );
}

function Separator({ className }: { className?: string }) {
  return <div className={cn('h-[1px] w-full bg-[#EBEBE6] lg:bg-[#EBEBE6]', className)} />;
}
